import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Window-OS template — the card looks like a macOS desktop window.
 *
 *   ┌─ ● ● ●  ~/ profile                         ─┐
 *   ├ sidebar ┬ main panel                         ┤
 *   │ Profile │ [avatar]  username + badges        │
 *   │ Music   │ description                        │
 *   │ Links   │ socials row                        │
 *   │ About   │ widgets grid                       │
 *   └─────────┴──────────────────────────────────┘
 *
 * Purely decorative — the sidebar items are static labels, not
 * navigation. The goal is the familiar desktop-window vibe, not a
 * functional OS simulator.
 */
export function renderWindowOs(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--window-os");

  const titleBar = el("div", { class: "winos__titlebar" }, [
    el("span", { class: "winos__dot winos__dot--r" }),
    el("span", { class: "winos__dot winos__dot--y" }),
    el("span", { class: "winos__dot winos__dot--g" }),
    el("span", { class: "winos__title" }, [
      `~/ ${config.profile.username} — ${config.meta.title || "bio"}`,
    ]),
  ]);

  // Static sidebar labels (purely aesthetic). The highlighted row matches
  // the template's "content" pane.
  const sidebar = el("div", { class: "winos__sidebar" }, [
    el("div", { class: "winos__sidebar-section" }, ["favorites"]),
    el("div", { class: "winos__sidebar-item winos__sidebar-item--active" }, ["👤 profile"]),
    el("div", { class: "winos__sidebar-item" }, ["🎵 music"]),
    el("div", { class: "winos__sidebar-item" }, ["🔗 links"]),
    el("div", { class: "winos__sidebar-item" }, ["🌐 socials"]),
    el("div", { class: "winos__sidebar-section" }, ["info"]),
    el("div", { class: "winos__sidebar-item" }, ["📡 activity"]),
    el("div", { class: "winos__sidebar-item" }, ["⚙️  settings"]),
  ]);

  const main = el("div", { class: "winos__main" });

  const header = el("div", { class: "winos__header" }, [
    p.avatar,
    el("div", { class: "winos__identity" }, [
      el("div", { class: "card__name-row" }, [p.username, p.badges]),
      p.description,
    ]),
  ]);

  const metaRow = el("div", { class: "card__meta" });
  const vc = p.meta.getInline("views");    if (vc) metaRow.append(vc);
  const loc = p.meta.getInline("location"); if (loc) metaRow.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) metaRow.append(clk);
  const ct = p.meta.getInline("contact");
  if (ct && p.contact) {
    const corner = el("div", { class: "card__corner card__corner--top-right" }, [ct]);
    p.card.append(corner);
  }

  main.append(header);
  if (metaRow.childElementCount > 0) main.append(metaRow);
  if (p.socials) main.append(p.socials);
  if (p.widgets) main.append(p.widgets);
  if (p.links) main.append(p.links);
  if (p.audio) main.append(p.audio);

  const body = el("div", { class: "winos__body" }, [sidebar, main]);
  p.card.append(titleBar, body);
  p.meta.applyCornerZones();
  return p.card;
}
